import React from 'react';
import { OwcTable, OwcTableCell, OwcTableHeader, OwcTableHeaderCell, OwcTableBody, OwcTableRow, OwcTypography} from '@one/react';
import DeviceMoveIcon from './DeviceMoveIcon.js';
import DelayedTooltip from '../general/DelayedTooltip.js';
import '../../styles.scss';

import {configData} from "../../config.js";
import {formatDate, convertToDate, fetchSigned, isRecentMessage} from "../../shared/Utilities.js"
import { renderToString } from 'react-dom/server'

/**
 * Renders the device history table
 *
 * @copyright Roche 2023
 * @author Nick Draper
 */
class DeviceHistory extends React.Component {
  /**
   * Constructor 
   * 
   * @param props The properties passed
   */
  constructor(props) {
    super(props);
    this.state = {
      historyLoaded: false,
      history: [],
    };
  }


  componentDidMount() {
    if (this.props.deviceId !== null) {
      console.log(`componentDidMount ${this.props.deviceId}`);
      this.loadDeviceHistory(this.props.deviceId);
    }
  }

  /** Runs whenever the properties of the control are changed
   * @param prevProps The previous properties dictionary
   * @param prevState The previous state dictionary
   */
  componentDidUpdate(prevProps, prevState) {
    if (prevProps.deviceId !== this.props.deviceId ) {
      if (isRecentMessage(this.props.forceUpdate)) {
        if (this.props.deviceId !== null) {
          console.log(`componentDidUpdate ${this.props.deviceId}`);
          this.loadDeviceHistory(this.props.deviceId);
        } else {
          this.setState({
            historyLoaded: false,
            history: [],
          });
        }
      }

    }
  }

  
  /**
   * Loads the history data to display for a device
   * @param {*} deviceId the device_id to load
   */
  loadDeviceHistory(deviceId) {
    console.log("loading device Life Cycle for ", deviceId, this.props.device_id);
    this.setState({ historyLoaded: false, history: []});

    fetchSigned(configData.DEVICE_DETAILS_API_URL + "lifecycle/" + deviceId + "/")
    .then(res => res.json())
    .then(
      (result) => {
        result.forEach(row=> {row.formattedAccounts = this.getAccountsText(row)});
        this.setState({ history: result,  
                        historyLoaded: true});
      },
      // Note: it's important to handle errors here
      // instead of a catch() block so that we don't swallow
      // exceptions from actual bugs in components.
      (error) => {
        this.setState({
          error
        });
      }
    )
  }


  /**
   * Generates and returns the text to be used to describe the accounts related to this device
   */
  getAccountsText(item) {
    const messageList = [];
    if (item.shipToAccountNo !== null) {
      messageList.push("shipTo: " + item.shipToAccountNo);
    }
    if (item.soldToAccountNo !== null) {
      messageList.push("soldTo: " + item.soldToAccountNo);
    }
    if (item.billToAccountNo !== null) {
      messageList.push("billTo: " + item.billToAccountNo);
    }
    if (item.instrumentLocationAccountNo !== null) {
      messageList.push("Location: " + item.instrumentLocationAccountNo);
    }
    if (messageList.length > 0){
      return(
        <span>
          {messageList.map((message, index) =>(<pre  key={item.deviceId + index} style={{marginTop:"0",marginBottom:"0"}}>{message}</pre>))}
        </span>);
    } else {
      return(
        <span style={{overflowWrap:"normal"}}>
          None
        </span>
      );
    }

  }

  /**
   * Detemines the background colour for a row
   */
  applyColour(row, previousRow, fieldKey, styleDict={}) {
    if (row.baselineConfirmationDate!==null)
    {
      styleDict.backgroundColor = configData.COLOUR_GREEN;
      
    } else {
      if ((previousRow !== null) &&
          (renderToString(row[fieldKey]) !== renderToString(previousRow[fieldKey]))) {
            styleDict.backgroundColor = configData.COLOUR_YELLOW;
      }
    }
    return styleDict;
  }

  /**
   * Determines if a row should have a device move signal displayed and returns the device move signal ison
   * @param row The device row for this timepoint
   * @param baselineRow The data row for the most recent baseline for this device
   * @returns JSX for a device move signal if one should be displayed
   */
  renderDeviceMoveSignal(row, baselineRow) {
    if (baselineRow !== null && baselineRow.baselineConfirmationDate !== null) {
      if (convertToDate(row.changeDate) > convertToDate(baselineRow.baselineConfirmationDate)) {
        if (baselineRow.instrumentLocationAccountNo !== row.instrumentLocationAccountNo && 
            baselineRow.labId !== row.labId) {
          return (
            <DeviceMoveIcon
              id={row.changeDate}
              iconTooltip={`Device move signal since the last location baseline of ${formatDate(baselineRow.baselineConfirmationDate)}`}
            />
          );
        }
      }
    }
  }

  extractSourceName (columnName, stringToRemove) {
    let source = columnName;
    if (stringToRemove) {
      source = source.replace(stringToRemove, "");
    }
    //convert camelCase to Sentence Case
    source = source.replace(/([A-Z])/g, " $1");
    source = source.charAt(0).toUpperCase() + source.slice(1);
    return source;
  }

  addSourceEntry(columnName, row) {
    const serialNoColumnName = columnName.replace("ChangeDate", "SerialNo");
    if (serialNoColumnName in row) {
      return(`${this.extractSourceName(columnName, "ChangeDate")}: ${row[serialNoColumnName]}`);
    } else {
      return(this.extractSourceName(columnName, "ChangeDate"));
    }
  }

  determineChangeTriggers(row, prevRow) {
    const sourceList = [];
    if (!prevRow) {
      //There is no previous row
      for (const [columnName, value] of Object.entries(row)) {
        if (columnName.endsWith("ChangeDate") && value) {
          sourceList.push(this.addSourceEntry(columnName, row));
        }
      }
    } else {
      //there is a previous row to compare against, just list those where the change date differs
      for (const [columnName, value] of Object.entries(row)) {
        if (columnName.endsWith("ChangeDate") && value && value !== prevRow[columnName]) {
          sourceList.push(this.addSourceEntry(columnName, row));
        }
      }
    }
    return (
      <>
        Change triggered by:<br/>
        {sourceList.map((row) => {return (<>{row}<br/></>)})}
      </>
    );


  }

  /**
   * Render the section displaying the device identity
   */
  renderHistoryRow(row, index, baselineRow) {
    const keyEnding = row.deviceId + row.changeDate;
    let prevRow = null;
    const prevIndex = index + 1;
    if (prevIndex > 0 && prevIndex < this.state.history.length) {
      prevRow = this.state.history[prevIndex];
    }
    const changeDateTooltip = this.determineChangeTriggers(row, prevRow);

    return (
      <OwcTableRow key={"HistoryRow" + keyEnding}>
        <OwcTableCell key={"ChangeDateCell" + keyEnding} 
            id={"ChangeDateCell" + keyEnding} 
            valign="middle"
            style={this.applyColour(row, null, "changeDate", {wordBreak:"normal"})}>
          <span key={"ChangeDateCellText" + keyEnding} id={"ChangeDateCellText" + keyEnding}>{formatDate(row.changeDate)}</span>
          {this.renderDeviceMoveSignal(row, baselineRow)}
        </OwcTableCell>
        <DelayedTooltip
          key={"ChangeDateCellTooltip" + keyEnding}
          anchor={"ChangeDateCellText" + keyEnding}
          placement={index===0?"right":"top"}
        >
          {row.baselineConfirmationDate!==null?
            <>
              {changeDateTooltip}<br/>Baseline confirmed on {formatDate(row.baselineConfirmationDate)}
            </>
          :
            <>{changeDateTooltip}</>
          }
        </DelayedTooltip>
        <OwcTableCell key={"LabIdCell" + keyEnding} valign="middle"
        style={this.applyColour(row, prevRow, "labId", {})}>{row.labId}</OwcTableCell>
        <OwcTableCell key={"MacAddressCell" + keyEnding} valign="middle"
        style={this.applyColour(row, prevRow, "macAddress", {wordBreak:"normal"})}>{row.macAddress}</OwcTableCell>
        <OwcTableCell key={"DMSerialNoCell" + keyEnding} valign="middle"
        style={this.applyColour(row, prevRow, "dataManagerSerialNo", {wordBreak:"normal"})}>{row.dataManagerSerialNo}</OwcTableCell>
        <OwcTableCell key={"RSDserialNoCell" + keyEnding} valign="middle"
        style={this.applyColour(row, prevRow, "remoteServiceDeviceSerialNo", {wordBreak:"normal"})}>{row.remoteServiceDeviceSerialNo}</OwcTableCell>
        <OwcTableCell key={"AccountNoCell" + keyEnding} id={"AccountNoCell" + keyEnding} valign="middle"
        style={this.applyColour(row, prevRow, "formattedAccounts", {wordBreak:"normal"})}>{row.formattedAccounts}</OwcTableCell>
        <DelayedTooltip key={"accountNoCellTooltip" + keyEnding}
          anchor={"AccountNoCell" + keyEnding} 
          disableTriggers = {(row.installedBaseMatchCount === 1)}>
            {row.installedBaseMatchCount === 1?
              ""
            :
              (<>Device details not enriched as this mapped to {row.installedBaseMatchCount} installed base records</>)
            }
        </DelayedTooltip>
        <OwcTableCell key={"AccountNameCell" + keyEnding} valign="middle"
        style={this.applyColour(row, prevRow, "locationAccountName", {wordBreak:"normal"})}>{row.locationAccountName}</OwcTableCell>
        <OwcTableCell key={"InstrumentAddressCell" + keyEnding} valign="middle"
        style={this.applyColour(row, prevRow, "instrumentAddress", {wordBreak:"normal"})}>{row.instrumentAddress}</OwcTableCell>
        <OwcTableCell key={"InstallDateCell" + keyEnding} valign="middle"
        style={this.applyColour(row, prevRow, "installDate", {wordBreak:"normal"})}>{formatDate(row.installDate)}</OwcTableCell>
        <OwcTableCell key={"PlacementTypeCell" + keyEnding} valign="middle"
        style={this.applyColour(row, prevRow, "placementType", {wordBreak:"normal"})}>{row.placementType}</OwcTableCell>
        <OwcTableCell key={"UserStatusCell" + keyEnding} valign="middle"
        style={this.applyColour(row, prevRow, "userStatus", {wordBreak:"normal"})}>{row.userStatus}</OwcTableCell>
      </OwcTableRow>
    );
  }

  /**
   * Renders the controls
   * @returns The JSX of the controls
   */
  render() {
    let history = this.state.history;
    if (history.length > this.props.historyLengthLimit) {
      history = history.slice(0, this.props.historyLengthLimit);
    }

    let baselineRow=null;
    history.forEach(row=>{
      if (row.baselineConfirmationDate !== null) {
        if (baselineRow === null) {
          baselineRow=row;
        } else {
          if (convertToDate(row.baselineConfirmationDate) > convertToDate(baselineRow.baselineConfirmationDate)) {
            baselineRow=row;
          }
        }
      }
    })

    const style = { maxWidth:"97%", marginLeft:"1em", height:"20em" };

    return (
      <OwcTable key={"DeviceHistoryTable" + this.props.deviceId + history.length} size='small' elevated
          cardProps={{ style: style }}>
        <OwcTableHeader sticky>
          <OwcTableHeaderCell key={"ChangeDateHeader"} resizable>Change Date</OwcTableHeaderCell>
          <OwcTableHeaderCell key={"LabIDHeader"} resizable>Lab Id</OwcTableHeaderCell>
          <OwcTableHeaderCell key={"MacAddressHeader"} resizable>Mac Address</OwcTableHeaderCell>
          <OwcTableHeaderCell key={"DMSerialNoHeader"} resizable>Data Manager Serial No</OwcTableHeaderCell>
          <OwcTableHeaderCell key={"RSDSerialNoHeader"} resizable>Remote Service Device Serial No</OwcTableHeaderCell>
          <OwcTableHeaderCell key={"AccountNoHeader"} resizable>Account No</OwcTableHeaderCell>
          <OwcTableHeaderCell key={"AccountNameHeader"} resizable>Account Name</OwcTableHeaderCell>
          <OwcTableHeaderCell key={"InstAddressHeader"} resizable>Device Location</OwcTableHeaderCell>
          <OwcTableHeaderCell key={"InstallDateHeader"} resizable>Install Date</OwcTableHeaderCell>
          <OwcTableHeaderCell key={"PlacementTypeHeader"} resizable>Placement Type</OwcTableHeaderCell>
          <OwcTableHeaderCell key={"UserStatusHeader"} resizable>User Status</OwcTableHeaderCell>
        </OwcTableHeader>
        <OwcTableBody loading={!this.state.historyLoaded}>
          {history.map((row,index) => this.renderHistoryRow(row, index, baselineRow))}
        </OwcTableBody>
        <div slot="header" style={{ alignItems: 'center' }}>
          <OwcTypography>
            {this.state.history.length > this.props.historyLengthLimit
              ?(`Most Recent ${history.length} of ${this.state.history.length>999?">":""}${this.state.history.length} Recorded Lifecycle Event${this.state.history.length===1?"":"s"}`)
              :(`${history.length} Recorded Lifecycle Event${history.length===1?"":"s"}`)
            }
          </OwcTypography>
        </div>
      </OwcTable>
    );

  }
}

DeviceHistory.defaultProps = {
  deviceId: null,
  forceUpdate: null,
  historyLengthLimit: 100,
}

export default DeviceHistory
